import React, { useEffect, useState } from 'react';
import axios from 'axios';
import get from 'lodash/get';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useParams } from 'react-router-dom';
import {
	SdfAlert,
	SdfBadge,
	SdfBox,
	SdfButton,
	SdfIcon,
	SdfIconButton,
	SdfLink,
	SdfSelectSimple,
	SdfShimmer,
	SdfSwitch,
	SdfTab,
	SdfTabGroup,
	SdfTooltip
} from '@synerg/react-components';
import withContext from '../common/withContext';
import ProjectContext from './projectContext';
import './credentials.scss';
import { payLoad } from './snippets';
import Toast from '../common/toast';
import Confirmation from '../common/confirmation';
import { uniqueId } from 'lodash';
import ProjDetailCertAlert from './projDetailCertAlert';
import ConsentPreview from './consentPreview';

function DataConnector({ context, setSelectedTab, setEndUserApiTabActive, allCertsInfo }) {
	// eslint-disable-next-line no-unused-vars
	const { projectId } = useParams();
	const [clientSecret, setClientSecret] = useState('');
	const [clientID, setClientID] = useState('');
	const [clientIdLoading, setClientIdLoading] = useState(false);
	const [clientSecretLoading, setClientSecretLoading] = useState(false);
	const [consumerApplication, setConsumerApplication] = useState({});
	const [showSecret, setShowSecret] = useState(false);
	const { projectDetail } = context;
	const { type, platform } = projectDetail;
	const [toast, setToast] = useState(false);
	const [openRegenerateConfirmation, setOpenRegenerateConfirmation] = useState(false);
	const [reqTabActive, setReqTabActive] = useState('request');
	const [showPreviewModal, setShowPreviewModal] = useState(false);
	const isDataCloudProject = platform === 'datacloud';
	const isDev = type === 'DEV';


	const codeSnippet = {
		'cURL': "curl --cert <your_certificate>.pem --key <your_key>.key -d 'grant_type=client_credentials&client_id=<your_client_id>&client_secret=<your_client_secret>' https://accounts.adp.com/auth/oauth/v2/token"
	};

	const handleError = (message) => {
		setToast({
			key: uniqueId(),
			status: "error",
			message: message,
			showToast: true
		});
	};

	const onShowSecret = event => {
		if (event.detail) {
			setShowSecret(true);
		}
		else {
			setClientSecret('');
			setShowSecret(false);
		}
	};

	const onSelectedTab = (event) => {
		if (event.detail.value) {
			setReqTabActive(event.detail.value);
		}
	};


	// regenerate credentials
	const regenerateCredentials = async () => {
		setClientSecretLoading(true);
		try {
			const { _id: projectID, b2bConsumerAppID: consumerApplicationID } = projectDetail;
			const { data: { clientSecret } } = await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/regenerate`, {
				projectID, consumerApplicationID
			});
			setClientSecret(clientSecret);
			setClientSecretLoading(false);
		} catch (e) {
			setClientSecretLoading(false);
			setClientSecret('');
			const errorMessage = get(e, 'response.data.message', 'Something went wrong while fetching certificates. Please try again.');
			handleError(errorMessage);
		}
	};


	// get application data call
	useEffect(() => {
		async function getApplicationDetails(projectID, consumerApplicationID) {
			try {
				const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/consumer-application`, {
					params: { projectID, consumerApplicationID }
				});
				setConsumerApplication(data);
			} catch (e) {
				const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get consumer application details. Please try again.');
				handleError(errorMessage);
			}
		}

		const { _id: projectId, b2bConsumerAppID } = projectDetail;
		if (projectId && b2bConsumerAppID) {
			getApplicationDetails(projectId, b2bConsumerAppID);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectDetail]);


	// get credentials
	useEffect(() => {
		async function getApplicationCredentials(projectID, consumerApplicationID) {
			setClientIdLoading(true);
			try {
				const { data } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/client-id`, {
					params: { projectID, consumerApplicationID }
				});
				setClientID(data.clientID);
				setClientIdLoading(false);
			} catch (e) {
				setClientIdLoading(true);
				const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get consumer application details. Please try again.');
				handleError(errorMessage);
			}
		}

		const { _id: projectId, b2bConsumerAppID } = projectDetail;
		if (projectId && b2bConsumerAppID) {
			getApplicationCredentials(projectId, b2bConsumerAppID);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectDetail]);

	// get client secret call

	useEffect(() => {
		async function getProjectCredentials(projectID, consumerApplicationID) {
			setClientSecretLoading(true);
			try {
				const { data: { clientSecret } } = await axios.get(`${import.meta.env.VITE_ROOT_PATH}/api/project/credentials/client-secret`, {
					params: { projectID, consumerApplicationID }
				});
				setClientSecret(clientSecret);
				setClientSecretLoading(false);
			} catch (e) {
				setClientSecret('');
				setClientSecretLoading(false);
				const errorMessage = get(e, 'response.data.message', 'Something went wrong when trying to get project credentials. Please try again.');
				handleError(errorMessage);
			}
		}

		const { _id: projectId, b2bConsumerAppID } = projectDetail;
		if (projectId && b2bConsumerAppID && showSecret) {
			getProjectCredentials(projectId, b2bConsumerAppID);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showSecret, projectDetail]);

	const { consentUrl = '' } = consumerApplication;
	const title = isDev
		? 'Getting Started with your Data Connector'
		: 'Getting your Data Connector Production Credentials';
	const subtitle = isDev
		? 'Follow these steps to exchange business to business data from ADP.'
		: 'Follow these steps to update your application to get ready for a production environment.';

	return (
		<div className="data-connector mt-12">
			{toast && <Toast
				key={toast.key}
				status={toast.status}
				message={toast.message}
				showToast={toast.showToast}
			/>}

			<div>
				<div className="text-heading-06 font-black leading-default">{title}</div>
				<div className="mt-2 step-container text-body-03">{subtitle}</div>

				<div className="mt-12 step-container text-heading-05 leading-sm font-bold">
					Step 1<br></br>
					{isDev ? "Obtain an access token from ADP  to call the credentials.read API" : "Update your test API credentials to production API credentials"}
				</div>

				<div className="step-container mt-4">
					<div className="min-w-0">
						<SdfBox
							shape="rounded"
							variant="bordered"
							spacing="loose"
						>
							<div className="h-auto">
								<div className="text-body-03">
									Use your client credentials below when calling the token endpoint.
								</div>
								<div className="flex justify-between items-center mt-8">
									<div className="text-heading-04 leading-xs font-bold">1. Your credentials</div>
									<div className="flex items-center">
										<span slot="label" className="text-heading-02 font-bold mr-4">Show secret </span>
										<SdfSwitch {...(clientSecretLoading ? { disabled: true } : "")} checked={!!clientSecret} slot="action" className="cursor-pointer" onSdfChange={(event) => { onShowSecret(event) }} >
											{clientSecret ? <SdfIcon icon="action-confirm" style={{ position: 'absolute', right: '12px', fontSize: '12px' }}></SdfIcon> :
												<SdfIcon icon="action-close" style={{ position: 'absolute', left: '4px', fontSize: '12px' }}></SdfIcon>
											}
										</SdfSwitch>
									</div>
								</div>

								<div className="client-credentials ml-12 mt-6">
									<div className="client-id-container grid gap-y-3">
										{clientIdLoading ? <div>
											<div className="w-8/12"><SdfShimmer /> </div>
											<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>Client ID</div>
										</div> :
											<div className="text-md font-bold break-all">
												{clientID}
												<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>
													Client ID
												</div>
											</div>
										}
										<div className="flex copy-client-id-container items-center justify-self-end">
											{clientID ? <CopyToClipboard text={clientID}>
												<SdfTooltip positionContext="ELEMENT" tooltipId={uniqueId} trigger="click" state="info">
													<SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
													Copied
												</SdfTooltip>
											</CopyToClipboard>
												:
												<SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>}
										</div>
									</div>

									<div className="border-1 border-dark border-solid my-6"></div>

									<div className="grid client-secret-container gap-y-3">
										{clientSecretLoading ? <div>
											<SdfShimmer />
											<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>Client Secret</div>
										</div> :
											clientSecret ? <div className='text-md font-bold break-all'>
												{clientSecret}
												<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>
													Client Secret
												</div>
											</div> :
												<div>
													{[...Array(10).keys()].map((i) => <span key={i} className="rounded-full bg-action-default-300 inline-block mr-2" style={{ height: "12px", width: "12px" }}></span>)}
													<div className="text-sm font-medium mt-1" style={{ opacity: '0.7' }}>
														Client Secret
													</div>
												</div>
										}
										<div className="regenerate-copy-container flex items-center col-gap-2 justify-self-end">
											<SdfButton {...(!clientSecret ? { disabled: true } : "")} emphasis="tertiary" icon="action-rotate-right" onClick={() => setOpenRegenerateConfirmation(true)}>Regenerate</SdfButton>
											{clientSecret ? <CopyToClipboard text={clientSecret}>
												<SdfTooltip positionContext="ELEMENT" attachmentPoint="bottom" trigger="click" state="info">
													<SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
													Copied
												</SdfTooltip>
											</CopyToClipboard> :
												<SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>}
										</div>
									</div>
								</div>

								{openRegenerateConfirmation && <Confirmation setOpenConfirmation={setOpenRegenerateConfirmation} confirmationMessage={"Regenerating your client secret will cause your existing integration to break. Click Yes if you want to proceed."} onConfirm={regenerateCredentials} />}


								<div className="mt-12 text-heading-04 leading-xs font-bold mt-12">
									2. Security certificate
								</div>

								<div className="ml-12 mt-6 mb-12">
									<ProjDetailCertAlert allCertsInfo={allCertsInfo} />
								</div>
							</div>
						</SdfBox>
					</div>

					<SdfBox
						shape="rounded"
						variant="filled"
						spacing="loose"
					>
						<div className="flex justify-between items-center gap-x-2">
							<div className="text-heading-06 font-black leading-xs">Token endpoint and code snippet</div>
							<SdfSelectSimple
								disabled
								items={[{ label: "cURL", value: "curl" }]}
								value={{ label: "cURL", value: "curl" }}
								className="w-1/4"
							/>
						</div>
						<div className="mt-4 text-body-02 break-all">https://accounts.adp.com/auth/oauth/v2/token</div>
						<div className="bg-action-default-700 p-5 mt-10 rounded-md">
							<div className="flex justify-end">
								<CopyToClipboard text={codeSnippet['cURL']}>
									<SdfTooltip positionContext="ELEMENT" trigger="click" state="info">
										<SdfIconButton slot="tooltip-target" style={{ color: "#FFFFFF" }}>
											<SdfIcon className="cursor-pointer" icon="action-copy" />
										</SdfIconButton>
										Copied
									</SdfTooltip>
								</CopyToClipboard>
							</div>
							<div className="box-curl payload flex justify-between items-center mt-6 mb-10 px-2" style={{ color: "#FFFFFF" }}>
								{codeSnippet['cURL']}
							</div>
						</div>
					</SdfBox>
				</div>

				{!isDataCloudProject && <div className="mt-16 step-container text-heading-05 leading-sm font-bold">
					Step 2<br></br>
					{isDev ? "Retrieve the newly subscribed organization’s client ID and client secret." : "Update consent manager URL"}
				</div>}

				{!isDataCloudProject && <div className="step-container mt-4">
					<div className="h-full">
						<SdfBox
							shape="rounded"
							variant="bordered"
							spacing="loose"
							className="px-8 h-full"
						>
							{isDev && <><div className="text-body-03">Use the access token and security certificate from step 1 to call the credentials.read API.</div>

								<div className='flex bg-gray-50 rounded-lg my-6 py-6 px-4 items-center'>
									<SdfBadge className='mr-2' status='complete'>POST</SdfBadge>
									<div className='text-heading-03 font-bold'>
										/events/core/v1/consumer-application-subscription-credentials.read
									</div>
								</div>
								<div className="text-body-02">
									We recommend using the subscribed client’s Organization OID (OOID) as a <SdfLink className="font-bold" href="https://developers.adp.com/articles/general/using-ooid-as-a-filter-parameter" target="_blank">filter</SdfLink> in the request payload. The response payload will include the subscribing organization’s client ID and client secret.
								</div>
								<SdfAlert status="warning" className="my-8">
									<div className="flex justify-between -mb-2">
										<div className="text-md">
											Explicit data consent is required by the client organization.
										</div>
										<SdfButton emphasis="secondary" className="-mt-2" onClick={() => setShowPreviewModal(true)}>Preview</SdfButton>
									</div>
								</SdfAlert>
								{showPreviewModal && <ConsentPreview setShowPreviewModal={setShowPreviewModal} />}
							</>}
							<div>
								<SdfBox
									shape="rounded"
									variant="filled"
									spacing="tight"
									className="	flex flex-col items-center justify-center px-8 bg-action-primary-50"
								>
									<div className="mt-4 text-center text-heading-04 font-bold"> This is your unique URL for data consent. The success URI
										can be changed to redirect to your product.
									</div>
									<p className="px-10 text-body-03 break-all">{consentUrl}</p>
									{consentUrl ? <CopyToClipboard text={consentUrl} className="mt-4 mb-2">
										<SdfTooltip positionContext="ELEMENT" attachmentPoint="bottom" trigger="click" state="info">
											<SdfButton emphasis="primary" slot="tooltip-target"> Copy </SdfButton>
											Copied
										</SdfTooltip>
									</CopyToClipboard> :
										<SdfButton disabled={true} emphasis="primary" slot="tooltip-target"> Copy </SdfButton>}
								</SdfBox>
								<div className="mt-8">
									<p className="text-body-02">The subscriber (i.e. ADP client practitioner) must provide consent before your app is able to succesfully fetch the client’s API credentials.</p>
									<p className="mt-8 text-body-02leading-md">
										ADP requires the HR practitioner from the subscribing organization to provide explicit consent to allow data exchange between applications like yours and ADP before using
										your application.</p>
								</div>
							</div>
						</SdfBox>
					</div>

					{isDev &&
						<SdfBox
							shape="rounded"
							variant="filled"
							spacing="loose"
							className="h-full"
						>
							<div className="flex justify-between items-center">
								<div className="text-heading-06 font-black leading-xs">Example of using OOID as a filter parameter</div>
							</div>
							<div className="mt-4">
								<SdfTabGroup size="small" onSdfChange={($event) => onSelectedTab($event)}>
									<SdfTab value="request" active={reqTabActive === "request"}>Example request</SdfTab>
									<SdfTab value="response" active={reqTabActive === "response"}>Sample response</SdfTab>
								</SdfTabGroup>
							</div>
							<div className="bg-action-default-700 p-5 mt-8 rounded-md">
								<div className="flex justify-end">
									<CopyToClipboard text={reqTabActive === "request" ? payLoad['request'] : payLoad['response']}>
										<SdfTooltip positionContext="ELEMENT" trigger="click" state="info">
											<SdfIconButton slot="tooltip-target" style={{ color: "#FFFFFF" }}>
												<SdfIcon className="cursor-pointer" icon="action-copy" />
											</SdfIconButton>
											Copied
										</SdfTooltip>
									</CopyToClipboard>
								</div>
								<div className="box-curl payload overflow-auto" style={{ color: "#FFFFFF", height: "665px" }}>
									{reqTabActive === "request" ? payLoad['request'] : payLoad['response']}
								</div>
							</div>
						</SdfBox>}
				</div>}

				<div>
					{!isDataCloudProject && isDev && <div className="mt-16 step-container text-heading-05 leading-sm font-bold">
						Step 3<br></br>
						Obtain another access token in the context of subscriber
					</div>}

					<div className="step-container">
						<div>
							{!isDataCloudProject && isDev && <SdfBox
								shape="rounded"
								variant="bordered"
								spacing="loose"
								className="p-8 mt-4"
							>
								<div className="text-body-03 mb-8">
									Use the client’s (subscriber’s) client ID and client secret obtained in step 2, and the security certificate from step 1 to call the token endpoint. This will provide you with the client’s (subscriber’s) access token. That token will be used to make API calls on the client’s behalf. For a full step-by-step guide and sample Postman collection on how to retrieve the credentials of a client (subscriber), please read more <SdfLink className="font-bold" href="https://developers.adp.com/articles/guides/how-to-retrieve-client-credentials-of-a-subscriber" target="_blank">here</SdfLink>.
								</div>
							</SdfBox>}

							<div className="mt-16">
								{isDev ? <div className="text-heading-05 leading-sm font-bold">
									{isDataCloudProject ? "Step 2" : "Step 4"} <br></br>
									Call ADP APIs to exchange data
								</div> :
									<div className="text-heading-05 leading-sm font-bold">
										{isDataCloudProject ? "Step 2" : "Step 3"}<br></br>
										Review ADP APIs in your scope
									</div>}
							</div>

							<div className="mt-4">
								<div>
									<SdfBox
										shape="rounded"
										variant="bordered"
										spacing="loose"
									>
										<div className="text-body-03">
											Use the API endpoint <b>https://api.adp.com/&#123;API_path&#125;</b> and the access token to begin calling the APIs you are authorized to call.
										</div>
										{isDev ? <div className="mt-12 text-right">
											<SdfLink className="mr-4 mb-2" href="https://partnersupport.adp.com/hc/en-us/articles/360036870553" target="_blank"> <SdfIcon icon="external-link" /> Need more APIs? </SdfLink>
											<SdfButton emphasis="primary" onClick={() => { setSelectedTab('Development APIs'); setEndUserApiTabActive(0) }}> View APIs you have access to </SdfButton>
										</div> : <div className="mt-12 text-right">
											<SdfButton emphasis="primary" onClick={() => { setSelectedTab('Production APIs'); setEndUserApiTabActive(0) }}> Review APIs you have access to </SdfButton>
										</div>}
									</SdfBox>
								</div>
							</div>
						</div>

						{!isDataCloudProject && isDev && <div className="mt-4">
							<SdfAlert
								status='info'
								size='sm'
								aria-live="polite"
								closeable="true"
							>
								<div slot="title" className="text-lg">How do I get a subscriber organization OID (OOID)?</div>
								<div className="text-sm mt-2">For development purposes, a test client will be subscribed to your project. You will use the test client’s OOID to filter the credentials.read API response.</div>

								<div className="my-6 text-sm">In production, your app will be notified when an ADP client subscribes to your integration. The notification will include the OOID you need to use as a filter when calling the credentials.read API as described in step 2.</div>

								<div className="mb-4 text-sm">NOTE: By default, your organization is subscribed to your app. In the absence of a test client, you can use your own OOID to filter the credentials.read API response. You will need to wait to call the APIs associated with your use case until a test instance has been subscribed to your project.</div>

							</SdfAlert>
						</div>}
					</div>
				</div>
			</div>
		</div>
	);
}

export default withContext(DataConnector, ProjectContext);
